import { ROLES } from "../config/constants";
import { formatDate } from "./timeHelpers";

// --- 輔助：四捨五入到小數點後兩位 ---
const roundToTwo = (num) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

// --- 勞基法第 38 條：特休天數對照表（依年資） ---
// tenureYears: 剛滿的年資（整數，例如剛滿 2 年傳 2）
export const getStatutoryAnnualLeaveDays = (tenureYears) => {
  const n = Math.floor(Number(tenureYears) || 0);
  if (n < 1) return tenureYears >= 0.5 ? 3 : 0;
  if (n === 1) return 7;
  if (n === 2) return 10;
  if (n >= 3 && n <= 4) return 14;
  if (n >= 5 && n <= 9) return 15;
  // 滿 10 年起每滿一年 +1，最多 30
  return Math.min(30, 15 + (n - 9));
};

// --- 判斷某 (年,月) 是否為員工的入職週年月 ---
// hireDateStr: "YYYY-MM-DD"
// year, month0(0-based): 要檢查的年月
// 回傳: { isAnniversary: bool, yearsCompleted: number }
export const getAnniversaryInfo = (hireDateStr, year, month0) => {
  if (!hireDateStr) return { isAnniversary: false, yearsCompleted: 0 };
  const hire = new Date(hireDateStr);
  if (isNaN(hire.getTime())) return { isAnniversary: false, yearsCompleted: 0 };
  if (hire.getMonth() !== month0) return { isAnniversary: false, yearsCompleted: 0 };
  const yearsCompleted = year - hire.getFullYear();
  if (yearsCompleted <= 0) return { isAnniversary: false, yearsCompleted: 0 };
  return { isAnniversary: true, yearsCompleted };
};

// --- 取得「下個月」相對於指定年月的滿週年資訊（給正職用） ---
// 涵蓋兩種觸發：(a) 入職滿週年；(b) 入職滿 6 個月（法定第一次給特休）
// 假設：上一年度未休完的特休已歸零，週年時為全新發放 → daysToAdd = 該年度法定天數
// 回傳: 物件含 { willBeAnniversary, type, yearsWillComplete?, daysToAdd, ... } 或 null
export const getNextMonthAnniversaryHint = (hireDateStr, year, month0) => {
  if (!hireDateStr) return null;
  const hire = new Date(hireDateStr);
  if (isNaN(hire.getTime())) return null;

  // 計算下個月
  const next = new Date(year, month0 + 1, 1);
  const nextYear = next.getFullYear();
  const nextMonth0 = next.getMonth();

  // (a) 年度週年：下個月 = 入職月 (年數 >= 1)
  const yearlyInfo = getAnniversaryInfo(hireDateStr, nextYear, nextMonth0);
  if (yearlyInfo.isAnniversary) {
    const newDays = getStatutoryAnnualLeaveDays(yearlyInfo.yearsCompleted);
    return {
      willBeAnniversary: true,
      type: 'yearly',
      yearsWillComplete: yearlyInfo.yearsCompleted,
      nextYear,
      nextMonth: nextMonth0 + 1,
      newAnnualLeaveDays: newDays,
      daysToAdd: newDays,
    };
  }

  // (b) 滿 6 個月：下個月 = 入職月 + 6 個月（同一年內）
  const halfDate = new Date(hire);
  halfDate.setMonth(hire.getMonth() + 6);
  if (halfDate.getFullYear() === nextYear && halfDate.getMonth() === nextMonth0) {
    return {
      willBeAnniversary: true,
      type: 'half_year',
      nextYear,
      nextMonth: nextMonth0 + 1,
      newAnnualLeaveDays: 3,
      daysToAdd: 3,
    };
  }

  return null;
};

// --- 取得「上上年」的工作期區間 (依到職日週年) ---
// 規則：當員工於本月剛滿 N 年時，「上上年」 = [hire + (N-2) 年, hire + (N-1) 年)
//   = 第 (N-1) 個工作年
//   例：入職 2022/05/15，今年滿 4 年（2026/05），上上年 = 2023/05/15 ~ 2024/05/14
// 回傳: { start: Date, end: Date }（end 為不含的上界）或 null（年資不足 2 年）
export const getPriorPriorYearRange = (hireDateStr, yearsCompleted) => {
  if (!hireDateStr || yearsCompleted < 2) return null;
  const hire = new Date(hireDateStr);
  if (isNaN(hire.getTime())) return null;
  const start = new Date(hire);
  start.setFullYear(hire.getFullYear() + (yearsCompleted - 2));
  const end = new Date(hire);
  end.setFullYear(hire.getFullYear() + (yearsCompleted - 1));
  return { start, end };
};

// --- 工讀生「特休換算薪資」計算（純函式） ---
// 公式: (上上年總時數 ÷ 2088) × 滿年資特休天數 × 8 小時 × 上上年平均時薪
//      其中 平均時薪 = 上上年總薪資 ÷ 上上年總時數
// 參數:
//   yearsCompleted - 剛滿幾年
//   totalHours - 上上年總工時
//   totalWage - 上上年總薪資（從歷史薪資單加總；若無，可用 hourlyRate × totalHours 估算）
// 回傳: { amount, days, avgHourlyRate, ratio } 或 null（資訊不足）
export const computePartTimeAnnualLeavePayout = ({
  yearsCompleted,
  totalHours,
  totalWage,
}) => {
  if (!totalHours || totalHours <= 0) return null;
  const days = getStatutoryAnnualLeaveDays(yearsCompleted);
  if (days <= 0) return null;
  const avgHourlyRate = totalWage / totalHours;
  const ratio = totalHours / 2088;
  const amount = Math.round(ratio * days * 8 * avgHourlyRate);
  return { amount, days, avgHourlyRate: Math.round(avgHourlyRate), ratio: roundToTwo(ratio) };
};

// --- 輔助：將 IN 打卡時間規整 ---
// 規則：3 分鐘容忍 → 退到該半點；其餘進到下一個半點
//   例：09:01~09:03 → 09:00；09:04~09:30 → 09:30；
//       09:31~09:33 → 09:30；09:34~09:59 → 10:00
export const roundPunchInTime = (date) => {
  const d = new Date(date);
  const m = d.getMinutes();
  if (m > 0 && m <= 3) d.setMinutes(0);
  else if (m > 30 && m <= 33) d.setMinutes(30);
  else if (m > 3 && m < 30) d.setMinutes(30);
  else if (m > 33) {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  }
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

// --- 輔助：將 OUT 打卡時間規整 ---
// 規則：< 30 分 → 退到該小時；>= 30 分 → 退到該半點
//   例：17:00~17:29 → 17:00；17:30~17:59 → 17:30
export const roundPunchOutTime = (date) => {
  const d = new Date(date);
  const m = d.getMinutes();
  if (m < 30) d.setMinutes(0);
  else d.setMinutes(30);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

// --- 工讀生單日工時計算（單一來源，HoursView 與薪資頁共用） ---
// 規則 A：IN 進到下個半點、OUT 退到上個半點，跨整段午休 (12:00-13:30) 扣 1.5h。
// 回傳 { hours, ranges }；ranges 為 [{ inStr, outStr }] 字串對，方便顯示。
export const calculatePartTimeWorkedRanges = (dayLogs) => {
  const validLogs = (dayLogs || [])
    .filter((l) => !l.isIgnored)
    .sort((a, b) => a.dateObj - b.dateObj);

  let totalHours = 0;
  const ranges = [];
  let currentIn = null;

  validLogs.forEach((log) => {
    if (log.type === "in") {
      if (!currentIn) currentIn = roundPunchInTime(log.dateObj);
    } else if (log.type === "out" && currentIn) {
      const out = roundPunchOutTime(log.dateObj);
      if (out > currentIn) {
        let dur = (out - currentIn) / 3600000;
        const ls = new Date(currentIn);
        ls.setHours(12, 0, 0, 0);
        const le = new Date(currentIn);
        le.setHours(13, 30, 0, 0);
        if (currentIn < ls && out > le) dur -= 1.5;
        if (dur > 0) {
          totalHours += dur;
          ranges.push({ in: currentIn, out });
        }
      }
      currentIn = null;
    }
  });

  return {
    hours: parseFloat(totalHours.toFixed(1)),
    ranges,
  };
};

// 簡化呼叫：只要小時數
const calculatePartTimeHours = (dayLogs) =>
  calculatePartTimeWorkedRanges(dayLogs).hours;

// --- 主函式：計算薪資統計數據 ---
export const calculatePayrollStats = (
  userProfile,
  logs,
  leaves,
  year,
  month
) => {
  // 1. 篩選當月 Logs
  const monthLogs = logs.filter((l) => {
    const d = l.dateObj;
    return d.getMonth() === month && d.getFullYear() === year && !l.isIgnored;
  });

  // 若當月完全沒有打卡記錄，回傳 hasLogs: false
  if (monthLogs.length === 0) {
    return { totalLateCount: 0, totalLateMinutes: 0, totalHours: 0, leaveStats: {}, hasLogs: false };
  }

  // 2. 篩選當月 Leaves (已核准)
  const monthLeaves = leaves.filter((l) => {
    const d = new Date(l.startDate);
    return (
      d.getMonth() === month &&
      d.getFullYear() === year &&
      l.status === "approved"
    );
  });

  // 3. 建立請假統計
  const leaveStats = {};
  monthLeaves.forEach((l) => {
    if (!leaveStats[l.type]) leaveStats[l.type] = { days: 0, hours: 0 };
    if (l.days > 0) {
      leaveStats[l.type].days += Number(l.days);
    } else {
      leaveStats[l.type].hours += Number(l.hours);
    }
  });

  let totalLateCount = 0;
  let totalLateMinutes = 0;
  let totalHours = 0;

  // 每日打卡歸戶
  const logsByDay = {};
  monthLogs.forEach((l) => {
    const dStr = formatDate(l.dateObj);
    if (!logsByDay[dStr]) logsByDay[dStr] = [];
    logsByDay[dStr].push(l);
  });

  const sortedDates = Object.keys(logsByDay).sort();

  sortedDates.forEach((dateStr) => {
    const dayLogs = logsByDay[dateStr];

    // A. 工讀生工時
    if (userProfile.role !== ROLES.FULLTIME) {
      totalHours += calculatePartTimeHours(dayLogs);
    }

    // B. 正職遲到 (依遲到分鐘數計算)
    if (userProfile.role === ROLES.FULLTIME) {
      const firstIn = dayLogs
        .sort((a, b) => a.dateObj - b.dateObj)
        .find((l) => l.type === "in");

      if (firstIn) {
        const stdStart = new Date(firstIn.dateObj);
        stdStart.setHours(9, 0, 0, 0);

        const hasLeaveToday = monthLeaves.some((l) => {
          const leaveDate = new Date(l.startDate);
          return formatDate(leaveDate) === dateStr;
        });

        if (!hasLeaveToday && firstIn.dateObj > stdStart) {
          const diffMinutes = Math.floor((firstIn.dateObj - stdStart) / 60000);
          if (diffMinutes > 0) {
            totalLateCount++;
            totalLateMinutes += diffMinutes;
          }
        }
      }
    }
  });

  return {
    totalLateCount,
    totalLateMinutes,
    totalHours: roundToTwo(totalHours),
    leaveStats,
    hasLogs: true,
  };
};

// --- 勞保加保日數／比例（到職月與離職月按比例）---
// 依勞保局實務：加保未滿一個月者，保險費按實際加保日數計算，並以 30 日為一個月。
//   到職月：加保日數 = 到職日 ~ 月底（含到職當日）
//   離職月：加保日數 = 月初 ~ 離職日（含離職當日）
//   同月到職又離職：到職日 ~ 離職日
// 回傳 null 代表「整月在保」，不做比例、照全額收取。
export const getInsuredDaysInMonth = (startDateStr, endDateStr, year, month0) => {
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  const monthStart = new Date(year, month0, 1);
  const monthEnd = new Date(year, month0, daysInMonth);

  const parse = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  const start = parse(startDateStr);
  const end = parse(endDateStr);

  // 尚未到職，或已於本月之前離職 → 本月完全未在保
  if (start && start > monthEnd)
    return { days: 0, ratio: 0, isHireMonth: false, isLeaveMonth: false, daysInMonth };
  if (end && end < monthStart)
    return { days: 0, ratio: 0, isHireMonth: false, isLeaveMonth: true, daysInMonth };

  const isHireMonth =
    !!start && start.getFullYear() === year && start.getMonth() === month0;
  const isLeaveMonth =
    !!end && end.getFullYear() === year && end.getMonth() === month0;

  // 非到職月也非離職月 → 整月在保
  if (!isHireMonth && !isLeaveMonth) return null;

  const from = isHireMonth ? start.getDate() : 1;
  const to = isLeaveMonth ? end.getDate() : daysInMonth;
  const days = Math.max(0, to - from + 1);
  return {
    days,
    // 勞保：以 30 日為一個月（勞保局實務），全月在保時上限為 1
    ratio: Math.min(1, days / 30),
    // 薪資：以當月實際天數為分母
    salaryRatio: Math.min(1, days / daysInMonth),
    isHireMonth,
    isLeaveMonth,
    daysInMonth,
  };
};

// --- 預先登記的加減項：薪資單產生時自動併入 ---
// adjustment 形如 { plusItems: [{name, amount}], minusItems: [{name, amount}] }
// 併入後重算 net。無資料時原樣回傳。
export const applyAdjustments = (salaryData, adjustment) => {
  if (!salaryData || !adjustment) return salaryData;
  const clean = (arr, fallbackName) =>
    (Array.isArray(arr) ? arr : [])
      .filter((i) => i && Number(i.amount))
      .map((i) => ({
        name: String(i.name || fallbackName),
        amount: Math.round(Number(i.amount) || 0),
        preRegistered: true,
      }));

  const plus = clean(adjustment.plusItems, "加項");
  const minus = clean(adjustment.minusItems, "減項");
  if (!plus.length && !minus.length) return salaryData;

  salaryData.plusItems = [...(salaryData.plusItems || []), ...plus];
  salaryData.minusItems = [...(salaryData.minusItems || []), ...minus];

  const plusTotal = salaryData.plusItems.reduce((a, p) => a + Number(p.amount || 0), 0);
  const minusTotal = salaryData.minusItems.reduce((a, p) => a + Number(p.amount || 0), 0);
  salaryData.net =
    (salaryData.gross || 0) + plusTotal - minusTotal - (salaryData.deductions?.total || 0);
  return salaryData;
};

// --- 主函式：計算薪資金額 ---
// year / month0 為選填；有帶入時，到職月與離職月的「勞保」會按加保日數比例計算。
export const calculateSalary = (userProfile, stats, daysInMonth, year, month0) => {
  const { role } = userProfile;
  const baseSalary = Number(userProfile.baseSalary) || 0;
  const hourlyRate = Number(userProfile.hourlyRate) || 0;
  const fullLaborInsurance = Number(userProfile.laborInsurance) || 0;
  const fullHealthInsurance = Number(userProfile.healthInsurance) || 0;

  // 到職月／離職月的保費與月薪處理：
  //   勞保：兩者皆按加保日數比例（以 30 日為一個月）
  //   健保：到職月整月收取；離職月不收（當月由轉入單位計收）
  //   月薪（正職）：離職月按在職天數／當月天數比例
  let laborInsurance = fullLaborInsurance;
  let healthInsurance = fullHealthInsurance;
  let laborProrate = null;
  let healthWaived = false;
  let salaryRatio = 1;
  let insuredInfo = null;
  if (year != null && month0 != null) {
    insuredInfo = getInsuredDaysInMonth(
      userProfile.startDate,
      userProfile.endDate,
      year,
      month0
    );
    if (insuredInfo) {
      laborInsurance = Math.round(fullLaborInsurance * insuredInfo.ratio);
      laborProrate = { days: insuredInfo.days, fullAmount: fullLaborInsurance };
      // 月薪：到職月與離職月皆按在職天數／當月天數比例
      salaryRatio = insuredInfo.salaryRatio ?? 0;
      // 健保：離職月免收（當月由轉入單位計收）；到職月維持整月收取
      if (insuredInfo.isLeaveMonth) {
        healthInsurance = 0;
        healthWaived = fullHealthInsurance > 0;
      }
    }
  }

  // 若無打卡記錄，回傳 null (PayrollView 會據此決定不產生薪資單)
  if (stats.hasLogs === false) {
    return null;
  }

  const { totalHours = 0, totalLateCount = 0, totalLateMinutes = 0, leaveStats = {} } = stats;

  let salaryData = {
    base: 0,
    gross: 0,
    deductions: {
      labor: laborInsurance,
      health: healthInsurance,
      // 有值代表本月勞保為比例計算（到職月／離職月），供薪資單顯示說明
      laborProrate: laborProrate,
      // 離職月免收健保時為 true，供薪資單顯示說明
      healthWaived: healthWaived,
      leaveDetails: [],
      late: 0,
      total: 0,
    },
    plusItems: [],
    minusItems: [],
    net: 0,
    details: [],
    lateCount: totalLateCount,
    lateMinutes: totalLateMinutes,
  };

  if (role === ROLES.FULLTIME) {
    const dailyRate = Math.round(baseSalary / daysInMonth);

    let leaveDeductTotal = 0;

    const addLeaveDeduct = (type, name, rateMultiplier) => {
      if (leaveStats[type]) {
        const dDays = Number(leaveStats[type].days) || 0;
        const dHours = Number(leaveStats[type].hours) || 0;
        const d = roundToTwo(dDays + dHours / 8);
        if (d > 0) {
          const amt = rateMultiplier === 0 ? 0 : Math.round(dailyRate * rateMultiplier * d);
          salaryData.deductions.leaveDetails.push({
            name: name,
            days: d,
            amount: amt,
          });
          return amt;
        }
      }
      return 0;
    };

    leaveDeductTotal += addLeaveDeduct("personal", "事假", 1);
    leaveDeductTotal += addLeaveDeduct("sick", "病假", 0.5);
    leaveDeductTotal += addLeaveDeduct("menstrual", "生理假", 0.5);
    addLeaveDeduct("annual", "特休", 0);

    // 遲到扣薪：底薪 / 當月天數 / 7.5小時 / 60分鐘 * 遲到分鐘數
    const perMinuteRate = baseSalary / daysInMonth / 7.5 / 60;
    const lateDeduct = Math.round(perMinuteRate * totalLateMinutes);

    // 離職月：月薪按在職天數／當月天數比例計算
    const proratedBase = salaryRatio < 1 ? Math.round(baseSalary * salaryRatio) : baseSalary;
    if (salaryRatio < 1) {
      salaryData.baseProrate = {
        days: insuredInfo?.days || 0,
        daysInMonth: insuredInfo?.daysInMonth || daysInMonth,
        fullAmount: baseSalary,
      };
    }

    salaryData.base = proratedBase;
    salaryData.gross = proratedBase;
    salaryData.deductions.late = lateDeduct;
    salaryData.deductions.total = laborInsurance + healthInsurance + leaveDeductTotal + lateDeduct;
    salaryData.net = salaryData.gross - salaryData.deductions.total;

  } else {
    // 工讀生
    const wage = Math.round(hourlyRate * totalHours);

    // 工讀生遲到扣薪：時薪 / 60 * 遲到分鐘數
    const lateDeduct = Math.round((hourlyRate / 60) * totalLateMinutes);

    salaryData.base = wage;
    salaryData.gross = wage;
    salaryData.deductions.late = lateDeduct;
    salaryData.deductions.total = laborInsurance + healthInsurance + lateDeduct;
    salaryData.net = salaryData.gross - salaryData.deductions.total;
    salaryData.details.push({ label: "時薪", val: hourlyRate });
    salaryData.details.push({ label: "該月上班總時數", val: totalHours });
    salaryData.details.push({ label: "薪資 (時薪*時數)", val: wage });
  }

  return salaryData;
};